package com.evoapps.evoshift.widget

import com.evoapps.evoshift.domain.usecase.shift.ClockPick
import com.evoapps.evoshift.domain.usecase.shift.ClockRules
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * What the phone sends the Wear OS app (SH-99) over the Wearable Data Layer ([WatchLink]): the
 * signed-in person's published shifts with their clock times, from the start of this week. The
 * watch has no account of its own; it shows this copy (also without the phone) and asks the phone
 * to clock in or out. [WatchContent] picks what to show "now".
 */
@Serializable
data class WatchSnapshot(
    /** ISO instant the snapshot was made. */
    val generatedAt: String,
    /** false: signed out on the phone, the watch asks to sign in there. */
    val signedIn: Boolean = true,
    val userName: String = "",
    /** From Monday of this week (or yesterday, if earlier) for two weeks, any workplace. */
    val shifts: List<WidgetShift> = emptyList(),
    /**
     * SH-137: the language picked in the phone's app ("en", "pl", "uk"); the watch shows its texts
     * in it. Null: the watch's own language.
     */
    val language: String? = null
) {
    fun toBytes(): ByteArray = json.encodeToString(serializer(), this).encodeToByteArray()

    companion object {
        /** Signed out: nothing to show. */
        fun signedOut(generatedAt: String) = WatchSnapshot(generatedAt = generatedAt, signedIn = false)

        fun fromBytes(bytes: ByteArray?): WatchSnapshot? =
            bytes?.let { runCatching { json.decodeFromString(serializer(), it.decodeToString()) }.getOrNull() }
    }
}

/** How long until a shift starts or ends (whole minutes, rounded up so "0 min" never shows early). */
sealed interface Countdown {
    data class StartsIn(val minutes: Long) : Countdown

    /** In progress; [elapsed] is the share of the planned time already gone (0..1). */
    data class EndsIn(val minutes: Long, val elapsed: Float) : Countdown

    data object Over : Countdown
}

/** Pure selection logic of the watch app, tile and complication (tested in commonTest). */
object WatchContent {

    /** The next shift that has not ended yet: the one in progress, else the soonest. */
    fun next(snapshot: WatchSnapshot, now: LocalDateTime): WidgetShift? =
        snapshot.shifts.filter { it.endTime > now }.minByOrNull { it.start }

    /**
     * What the clock button offers at [now], by the same rules the phone then applies
     * ([ClockRules]); only shifts with an id can be clocked.
     */
    fun clock(snapshot: WatchSnapshot, now: LocalDateTime, zone: TimeZone): ClockPick<WidgetShift> =
        ClockRules.pick(snapshot.shifts.filter { it.id != null }, now, zone, times)

    /** The shifts of [today]'s week ([firstDay] first), in order. */
    fun week(snapshot: WatchSnapshot, today: LocalDate, firstDay: DayOfWeek = DayOfWeek.MONDAY): List<WidgetShift> {
        val back = (today.dayOfWeek.isoDayNumber - firstDay.isoDayNumber + 7) % 7
        val start = today.minus(DatePeriod(days = back))
        val end = start.plus(DatePeriod(days = 6))
        return snapshot.shifts.filter { it.date in start..end }.sortedBy { it.start }
    }

    fun countdown(shift: WidgetShift, now: LocalDateTime, zone: TimeZone): Countdown {
        val nowMs = now.toInstant(zone).toEpochMilliseconds()
        val startMs = shift.startTime.toInstant(zone).toEpochMilliseconds()
        val endMs = shift.endTime.toInstant(zone).toEpochMilliseconds()
        return when {
            nowMs < startMs -> Countdown.StartsIn(ceilMinutes(startMs - nowMs))
            nowMs < endMs -> Countdown.EndsIn(
                ceilMinutes(endMs - nowMs),
                ((nowMs - startMs).toFloat() / (endMs - startMs).coerceAtLeast(1)).coerceIn(0f, 1f)
            )
            else -> Countdown.Over
        }
    }

    private fun ceilMinutes(ms: Long): Long = (ms + 59_999) / 60_000

    private val times = ClockRules.Times { s: WidgetShift -> ClockRules.ShiftTimes(s.start, s.end, s.clockedIn, s.clockedOut) }
}

/**
 * The phone <-> watch contract over the Wearable Data Layer (SH-99). The phone app publishes
 * [WatchSnapshot] as a data item at [SNAPSHOT_PATH]; the watch sends requests (MessageClient
 * RPC) to the node advertising [PHONE_CAPABILITY]: [CLOCK_PATH] with a [WatchClockRequest]
 * (answered with a [WatchClockReply]) and [REFRESH_PATH] (empty; the phone republishes).
 */
object WatchLink {
    const val SNAPSHOT_PATH = "/evoshift/snapshot"
    const val CLOCK_PATH = "/evoshift/clock"
    const val REFRESH_PATH = "/evoshift/refresh"

    /** Advertised by the phone app (androidApp res/values/wear.xml). */
    const val PHONE_CAPABILITY = "evoshift_phone_app"

    /** Advertised by the watch app (wearApp res/values/wear.xml). */
    const val WATCH_CAPABILITY = "evoshift_wear_app"
}

@Serializable
data class WatchClockRequest(val shiftId: String, val action: Action) {
    /** SH-116: BREAK_START / BREAK_END while clocked in (an older phone app answers FAILED). */
    enum class Action { IN, OUT, BREAK_START, BREAK_END }

    fun toBytes(): ByteArray = json.encodeToString(serializer(), this).encodeToByteArray()

    companion object {
        fun fromBytes(bytes: ByteArray?): WatchClockRequest? =
            bytes?.let { runCatching { json.decodeFromString(serializer(), it.decodeToString()) }.getOrNull() }
    }
}

@Serializable
data class WatchClockReply(val outcome: Outcome) {
    /** What happened; the watch words it (the domain returns facts, not sentences). */
    enum class Outcome {
        DONE,
        /**
         * The phone has no connection: Firestore keeps the clock time and sends it once online
         * (offline persistence). Shown as done, with a note.
         */
        SAVED_OFFLINE,
        SIGNED_OUT,
        NOT_YOUR_SHIFT,
        TOO_EARLY,
        ALREADY_CLOCKED_IN,
        NOT_CLOCKED_IN,
        /** The workplace's plan ended: read-only (SH-65). */
        PLAN_ENDED,
        /** No phone with the app nearby (watch side only). */
        PHONE_UNREACHABLE,
        /** SH-106: the workplace checks the location and the watch has none: clock in on the phone. */
        LOCATION_REQUIRED,
        /** SH-106: farther from the workplace than it allows. */
        OUTSIDE_AREA,
        /** SH-116: a break is already going on / there is none to end. */
        ALREADY_ON_BREAK,
        NOT_ON_BREAK,
        FAILED;

        /** The clock time was taken (now or once the phone is online). */
        val clocked: Boolean get() = this == DONE || this == SAVED_OFFLINE
    }

    fun toBytes(): ByteArray = json.encodeToString(serializer(), this).encodeToByteArray()

    companion object {
        /** An unreadable reply (e.g. an older phone app) counts as a failure. */
        fun fromBytes(bytes: ByteArray?): WatchClockReply =
            bytes?.let { runCatching { json.decodeFromString(serializer(), it.decodeToString()) }.getOrNull() }
                ?: WatchClockReply(Outcome.FAILED)
    }
}

private val json = Json { ignoreUnknownKeys = true }
